import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  ConfirmationDialogSettingItem,
  ConfirmationDialogSettingsResponse,
  ConfirmationDialogType,
  Feature,
  SellerAccountClient,
} from 'src/WebApiController';

import { useUserHasFeature } from '../useUserHasFeature';

export const useConfirmationDialogSettings = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const hasSellerEventAlertMutes = useUserHasFeature(
    Feature.SellerEventAlertMutes
  );

  const queryClient = useQueryClient();
  const getQueryKey = [
    'SellerAccountClient.getConfirmationDialogSettings',
    activeAccountWebClientConfig.activeAccountId,
  ];

  const shouldQuery =
    activeAccountWebClientConfig.activeAccountId != null &&
    hasSellerEventAlertMutes;

  const getConfirmationDialogSettingsQuery = useQuery({
    queryKey: getQueryKey,
    queryFn: async () => {
      if (!shouldQuery || !hasSellerEventAlertMutes) {
        return null;
      }

      const response = await new SellerAccountClient(
        activeAccountWebClientConfig
      ).getConfirmationDialogSettings();

      return response;
    },
    enabled: shouldQuery,
    staleTime: Infinity, // This is per account and it is updated via callbacks in mutation
    refetchOnWindowFocus: false,
    meta: {
      persist: false,
      onError: (error: ErrorTypes) => {
        showErrorDialog(
          'SellerAccountClient.getConfirmationDialogSettings',
          error,
          {
            trackErrorData: {
              activeAccountId: activeAccountWebClientConfig.activeAccountId,
            },
          }
        );
      },
    },
  });

  const upsertConfirmationDialogSettingsMutation = useMutation({
    mutationFn: async ({
      settings,
    }: {
      settings: Record<ConfirmationDialogType, ConfirmationDialogSettingItem>;
    }) => {
      const currentData =
        queryClient.getQueryData<ConfirmationDialogSettingsResponse>(
          getQueryKey
        );
      const metadata = currentData?.metadata || [];

      const settingsArray = Object.entries(settings).map(([key, value]) => {
        const dialogType = key as ConfirmationDialogType;
        const metaInfo = metadata.find((m) => m.dialogType === dialogType);

        return {
          confirmationDialogType: dialogType,
          isActive: value.isActive,
          absoluteThreshold:
            metaInfo?.thresholdType === 'Absolute'
              ? (value.absoluteThreshold ?? null)
              : null,
          relativeThreshold:
            metaInfo?.thresholdType === 'Relative'
              ? (value.relativeThreshold ?? null)
              : null,
        };
      });

      const request = {
        settings: settingsArray,
      };

      const client = new SellerAccountClient(activeAccountWebClientConfig);
      return await client.upsertConfirmationDialogSettings(request);
    },
    onMutate: ({ settings }) => {
      queryClient.cancelQueries({ queryKey: getQueryKey });
      const prevValue =
        queryClient.getQueryData<ConfirmationDialogSettingsResponse>(
          getQueryKey
        );

      if (prevValue) {
        queryClient.setQueryData(getQueryKey, {
          ...prevValue,
          settings,
        });
      }
      return { prevValue };
    },
    onError: (err: ErrorTypes, { settings }, context) => {
      queryClient.setQueryData(getQueryKey, context?.prevValue);
      showErrorDialog(
        'SellerAccountClient.upsertConfirmationDialogSettings',
        err,
        {
          trackErrorData: { settings },
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getQueryKey });
    },
  });

  return {
    confirmationDialogSettings: getConfirmationDialogSettingsQuery.data,
    updateConfirmationDialogSettings:
      upsertConfirmationDialogSettingsMutation.mutate,
    isLoading: getConfirmationDialogSettingsQuery.isLoading,
    isPending:
      getConfirmationDialogSettingsQuery.isFetching ||
      upsertConfirmationDialogSettingsMutation.isPending,
    loaded: getConfirmationDialogSettingsQuery.isFetched,
  };
};
